import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ClickPointsAnimation from '@/components/animations/ClickPointsAnimation';
import { useStreakTracking } from '@/hooks/use-streak-tracking';
import { sounds } from '@/utils/soundEffects';


interface BulletinLikeHandlerProps {
  bulletinId: string;
  initialLikeCount: number;
  userId: string | undefined;
  currentImageUrl?: string;
  currentThumbnailUrl?: string;
  loopId?: string | null;
  onLike?: () => void; // Callback when user likes the post
  children: (
    hasLiked: boolean,
    currentLikeCount: number,
    handleDoubleClick: (e: React.MouseEvent) => void
  ) => React.ReactNode;
}

const BulletinLikeHandler = ({
  bulletinId,
  initialLikeCount,
  userId,
  currentImageUrl,
  currentThumbnailUrl,
  loopId,
  onLike,
  children,
}: BulletinLikeHandlerProps) => {
  const [hasLiked, setHasLiked] = useState(false);
  const [currentLikeCount, setCurrentLikeCount] = useState(initialLikeCount);
  const [isLoading, setIsLoading] = useState(true);
  const [showPointsAnimation, setShowPointsAnimation] = useState(false);
  const [animationPosition, setAnimationPosition] = useState({ x: 0, y: 0 });
  const { toast } = useToast();
  const { recordLike } = useStreakTracking();
  

  const isLikingRef = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    isMounted.current = true;
    
    const loadingTimeout = setTimeout(() => {
      if (isMounted.current && isLoading) {
        console.log('Loading timeout reached, forcing loading state to false');
        setIsLoading(false);
      }
    }, 5000);

    const checkLikeStatus = async () => {
      if (!bulletinId) {
        console.log('Missing bulletinId, skipping like status check');
        setIsLoading(false);
        return;
      }
      
      if (!userId) {
        console.log('Missing userId, likely not authenticated, skipping like status check');
        setIsLoading(false);
        return;
      }

      console.log('Checking like status for bulletin:', bulletinId, 'user:', userId);
      
      // Fetch loop_id if not provided
      let targetLoopId = loopId;
      if (!targetLoopId) {
        const { data: bulletinData } = await supabase
          .from('bulletins')
          .select('loop_id')
          .eq('id', bulletinId)
          .maybeSingle();
        targetLoopId = bulletinData?.loop_id;
      }
      
      try {
        const { data, error } = await supabase
          .from('loops_likes')
          .select('id')
          .eq('loop_id', targetLoopId || bulletinId)
          .eq('user_id', userId)
          .maybeSingle();

        if (error) {
          console.error('Error checking like status:', error);
          if (isMounted.current) {
            toast({
              title: "Warning",
              description: "Could not verify like status.",
              variant: "destructive",
            });
          }
          return;
        }
        
        if (isMounted.current) {
          const liked = !!data;
          console.log('Initial like status for bulletin:', bulletinId, 'is:', liked, 'data:', data);
          
          setHasLiked(liked);
          
          if (liked) {
            setCurrentLikeCount(Math.max(1, initialLikeCount));
          } else {
            setCurrentLikeCount(initialLikeCount);
          }
        }
      } catch (error) {
        console.error('Error checking like status:', error);
        if (isMounted.current) {
          toast({
            title: "Error",
            description: "Could not verify like status. Please refresh.",
            variant: "destructive",
          });
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    checkLikeStatus();
    
    return () => {
      isMounted.current = false;
      clearTimeout(loadingTimeout);
    };
  }, [bulletinId, userId, initialLikeCount]);

  const handleDoubleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading || !bulletinId || !userId) {
      console.log('[BulletinLikeHandler] Aborted:', { isLoading, bulletinId, userId });
      return;
    }

    if (hasLiked) {
      console.log('[BulletinLikeHandler] Already liked, preventing animation');
      return;
    }

    if (isLikingRef.current) {
      console.log('[BulletinLikeHandler] Like operation in progress');
      return;
    }

    // ENHANCED DEBUGGING: Check authentication state before proceeding
    console.log('=== LIKE OPERATION DEBUG START ===');
    console.log('[DEBUG] Frontend userId prop:', userId);
    
    try {
      // Check current Supabase session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      console.log('[DEBUG] Current Supabase session:', {
        hasSession: !!sessionData.session,
        sessionUser: sessionData.session?.user?.id,
        sessionError: sessionError,
        accessToken: sessionData.session?.access_token ? 'present' : 'missing',
        refreshToken: sessionData.session?.refresh_token ? 'present' : 'missing'
      });

      // Check auth.uid() which is what RLS policies use
      const { data: authUidData, error: authUidError } = await supabase
        .rpc('get_current_user_id')
        .single();
      console.log('[DEBUG] auth.uid() via RPC:', { authUidData, authUidError });

      // Alternative way to check auth context
      const { data: testAuth, error: testAuthError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .single();
      console.log('[DEBUG] Test auth access to profiles:', { 
        canAccessOwnProfile: !testAuthError, 
        testAuthError: testAuthError?.message 
      });

    } catch (debugError) {
      console.error('[DEBUG] Error during authentication debugging:', debugError);
    }

    isLikingRef.current = true;

    if (isMounted.current) {
      setHasLiked(true);
      setCurrentLikeCount((prevCount) => Math.max(1, prevCount + 1));
      setAnimationPosition({ x: e.clientX, y: e.clientY });
      setShowPointsAnimation(true);
      sounds.like();
    }

    try {
      // Fetch loop_id if not provided
      let targetLoopId = loopId;
      if (!targetLoopId) {
        const { data: bulletinData } = await supabase
          .from('bulletins')
          .select('loop_id')
          .eq('id', bulletinId)
          .maybeSingle();
        targetLoopId = bulletinData?.loop_id;
      }

      if (!targetLoopId) {
        throw new Error('No loop_id found for bulletin');
      }

      console.log('[DEBUG] Attempting loops_likes insert with:', {
        loop_id: targetLoopId,
        user_id: userId,
        timestamp: new Date().toISOString()
      });

      const { data: insertData, error } = await supabase
        .from('loops_likes')
        .insert([{ loop_id: targetLoopId, user_id: userId }])
        .select();

      console.log('[DEBUG] Insert operation result:', {
        data: insertData,
        error: error ? {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        } : null
      });

      if (!error && isMounted.current) {
        console.log('[BulletinLikeHandler] Like registered successfully');
        recordLike();
        
        // Call onLike callback to notify parent component
        if (onLike) {
          onLike();
        }
        
        // Relic promotion is now handled automatically by the database trigger
      }

      if (error) {
        console.error('[BulletinLikeHandler] Error:', error);
        if (error.code === '23505') {
          console.log('[BulletinLikeHandler] Already liked (DB constraint)');
        } else if (isMounted.current) {
          setHasLiked(false);
          setCurrentLikeCount((prevCount) => Math.max(0, prevCount - 1));
          toast({
            title: "Error",
            description: `Failed to like the bulletin: ${error.message}`,
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('[BulletinLikeHandler] Unexpected error:', error);
      if (isMounted.current) {
        setHasLiked(false);
        setCurrentLikeCount(initialLikeCount);
        setShowPointsAnimation(false);
      }
    } finally {
      console.log('=== LIKE OPERATION DEBUG END ===');
      setTimeout(() => {
        if (isMounted.current) {
          isLikingRef.current = false;
        }
      }, 150);
    }
  };

  console.log(`[BulletinLikeHandler] Rendering state for ${bulletinId}: isLoading=${isLoading}, hasLiked=${hasLiked}, likeCount=${currentLikeCount}`);

  return (
    <>
      {children(hasLiked, currentLikeCount, handleDoubleClick)}
      <ClickPointsAnimation
        points={1}
        x={animationPosition.x}
        y={animationPosition.y}
        type="like"
        show={showPointsAnimation}
        onAnimationComplete={() => {
          if (isMounted.current) {
            setShowPointsAnimation(false);
          }
        }}
      />
    </>
  );
};

export default BulletinLikeHandler;
